import { Component } from "@theme/component";
import { isClickedOutside, onAnimationEnd, isMobileBreakpoint, waitForEvent, executeScriptsInElement, debounce, isDesktopBreakpoint, mediaQueryMobile } from '@theme/utilities';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { animate } from "@theme/vendor";
import { DialogOpenEvent, DialogCloseEvent, DialogBeforeCloseEvent, DropdownDialogOpenEvent, DropdownDialogCloseEvent} from '@theme/events';
import { bindCloseCursor, reviveCloseCursors } from '@theme/custom-cursor';
export class DialogComponent extends Component {
  static DRAG_CLOSE_RATIO = 0.2;

  #dialogOwnerId = '';
  #deselectTimeout = null;
  #touchStartY = 0;
  #currentTranslateY = 0;
  #dragThreshold = null;
  #dragCloseAt = 0;
  #dragRafId = null;
  #positionRafId = null;
  #zIndex = 0;

  /** @type {Node | null} */
  #originalDialogParent = null;

  /** @type {HTMLElement | null} */
  #appendedDialog = null;

  /** @type {HTMLElement | null} */
  #previousActiveElement = null;
  #cursorBinding = null;

  connectedCallback() {
    super.connectedCallback();
    this.#dialogOwnerId = this.id
      ? `dialog-owner-${this.id}`
      : `dialog-owner-${Math.random().toString(36).slice(2)}`;

    if (Shopify.designMode) {
      this.handleDesignMode();
    }

    this.#setupDesktopAutoClose();
  }

  #setupDesktopAutoClose() {
    const { dialog } = this.#getOwnDialogRefs();
    if (!dialog?.hasAttribute('data-close-desktop')) return;
    mediaQueryMobile.addEventListener('change', this.#handleDesktopChange);
  }

  #handleDesktopChange = (event) => {
    if (!event.matches) this.closeDialog();
  };

  /**
   * Resolve refs from this dialog instance only (direct structure),
   * so nested dialogs cannot override refs in Component.refs.
   */
  #getOwnDialogRefs() {
    const dialog =
      this.querySelector(':scope > [ref="dialog"]') ??
      document.querySelector(`[data-dialog-owner="${this.#dialogOwnerId}"]`);
    if (dialog) {
      dialog.setAttribute('data-dialog-owner', this.#dialogOwnerId);
    }
    const dialogOverlay = dialog?.querySelector(':scope > [ref="dialogOverlay"]');
    const dialogContainer = dialog?.querySelector(':scope > [ref="dialogContainer"]');
    const dialogHeader = dialogContainer?.querySelector(':scope > [ref="dialogHeader"]');
    const dialogInner = dialogContainer?.querySelector(':scope > [ref="dialogInner"]');
    const closeButton = dialogHeader?.querySelector(':scope > [ref="closeButton"]');
    const dialogTemplate = dialogInner?.querySelector(':scope > template[ref="dialogTemplate"]');

    return {
      dialog,
      dialogOverlay,
      dialogContainer,
      dialogHeader,
      dialogInner,
      closeButton,
      dialogTemplate,
    };
  }

  handleDesignMode() {
    this.handleBlockSelect = (event) => {
      if(event.target == this || this.classList.contains('dialog-component--skip-select')) return;
      if (this.contains(event.target)) {
        // Cancel pending close
        clearTimeout(this.#deselectTimeout);
        this.#deselectTimeout = null;

        this.openDialog();
      }
    };
    this.addEventListener('shopify:block:select', this.handleBlockSelect);

    this.handleBlockDeselect = (event) => {
      if(this.classList.contains('dialog-component--skip-deselect')) return;
      if (this.contains(event.target) ) {
        this.#deselectTimeout = setTimeout(() => {
          event.isDesignMode = true;
          this.closeDialog(event);
        }, 100);
      }
    };
    this.addEventListener('shopify:block:deselect', this.handleBlockDeselect);

    const section = this.closest('.shopify-section');
    if (!section) return;

    this.handleSectionLoad = (event) => {
      this.#updateScrollLock(false);
    };
    section.addEventListener('shopify:section:load', this.handleSectionLoad);

    this.handleSectionUnload = (event) => {
      this.#updateScrollLock(false);
    };
    section.addEventListener('shopify:section:unload', this.handleSectionUnload);

  }

  /**
   * Re-rendered by the Section Rendering API (e.g. editor option change). An
   * open append-to-body dialog lives in <body>, outside the morphed section, so
   * the morph injects a fresh closed dialog this component rebinds to and the
   * open one is orphaned. Dismiss the orphan so it doesn't stay stuck open.
   */
  updatedCallback() {
    super.updatedCallback();
    if (Shopify.designMode) this.#dismissAppendedDialog();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#deselectTimeout);
    this.#removeEventListeners();
    this.#dismissAppendedDialog();
    mediaQueryMobile.removeEventListener('change', this.#handleDesktopChange);

    // Cleanup design mode listeners
    if (this.handleBlockSelect) {
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
    }
    if (this.handleBlockDeselect) {
      this.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
    }
    if (this.handleSectionLoad) {
      const section = this.closest('.shopify-section');
      section?.removeEventListener('shopify:section:load', this.handleSectionLoad);
    }
    if (this.handleSectionUnload) {
      const section = this.closest('.shopify-section');
      section?.removeEventListener('shopify:section:unload', this.handleSectionUnload);
    }
  }

  #shouldAppendToBody() {
    if (this.hasAttribute('data-append-to-body')) return true;
    if (Shopify.designMode && this.closest('carousel-component')) return true;
    return false;
  }

  async openDialog(e) {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const { dialog, dialogInner, dialogContainer, dialogTemplate } = this.#getOwnDialogRefs();
    if (!dialog || !dialogContainer) return;

    const isOpen = dialog.hasAttribute('open');
    if (isOpen) return;

    this.#previousActiveElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    if (this.#shouldAppendToBody()) {
      this.#originalDialogParent = dialog.parentNode;
      const highestZIndex = this.#getHighestZIndex();
      this.#zIndex = highestZIndex + 1;
      dialog.style.zIndex = this.#zIndex;
      dialog.classList.add('append-to-body');
      document.body.appendChild(dialog);
      this.#appendedDialog = dialog;
    }

    if (dialogInner) {
      const templateElement = dialogTemplate;
      if (templateElement) {
        const fragment = templateElement.content.cloneNode(true);
        dialogInner.innerHTML = '';
        dialogInner.appendChild(fragment);
        executeScriptsInElement(dialogInner);
      }
    }

    this.#calculateScrollbarWidth();

    dialog.setAttribute('open', '');
    this.#updateScrollLock(true);
    this.#updateSectionDialogState(true);
    await onAnimationEnd(dialogContainer, undefined, {
      subtree: false,
    });
    dialogContainer.style.willChange = 'auto';
    this.dispatchEvent(new DialogOpenEvent());

    requestAnimationFrame(() => {
      this.#setupEventListeners();
      this.#focusDialog();
    });
  }

  /**
   * Sets up focus trap inside the dialog (Tab cycles within dialog).
   */
  #focusDialog() {
    const { dialog } = this.#getOwnDialogRefs();
    if (!dialog?.hasAttribute('open')) return;
    trapFocus(dialog, undefined, { focusImmediately: false });
  }

  /**
   * Nearest ancestor dialog still open behind this one. Matched by the
   * `.dialog-component` class, not the tag, so subclasses with a custom tag
   * (e.g. CartDrawerComponent) are also found.
   * @returns {DialogComponent | null}
   */
  #findAncestorDialogComponent() {
    return this.parentElement?.closest('.dialog-component') ?? null;
  }

  /**
   * Re-applies this dialog's focus trap if it is still open. Used to restore a parent
   * dialog's trap after a nested dialog closes, since the trap is a single shared handler.
   */
  restoreFocusTrap() {
    const { dialog } = this.#getOwnDialogRefs();
    if (dialog?.hasAttribute('open')) {
      trapFocus(dialog, undefined, { focusImmediately: false });
    }
  }

  /**
   * Sets up event listeners for dialog interactions
   */
  #removeEventListeners() {
    const { dialogOverlay, dialogHeader, closeButton } = this.#getOwnDialogRefs();
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener('keydown', this.#handleKeyDown);
    if (this.boundCloseDialog) {
      dialogOverlay?.removeEventListener('click', this.boundCloseDialog);
      closeButton?.removeEventListener('click', this.boundCloseDialog);
      this.boundCloseDialog = null;
    }
    if (dialogHeader) {
      dialogHeader.removeEventListener('touchstart', this.#onTouchStart);
      dialogHeader.removeEventListener('touchmove', this.#onTouchMove);
      dialogHeader.removeEventListener('touchend', this.#onTouchEnd);
    }
    this.#removeCursorListeners();
  }

  #setupEventListeners() {
    const { dialog, dialogOverlay, dialogHeader, closeButton } = this.#getOwnDialogRefs();

    this.#removeEventListeners();

    // Setup click and keyboard handlers
    this.addEventListener('click', this.#handleClick);
    // Use document for keydown to catch ESC even when dialog is appended to body
    document.addEventListener('keydown', this.#handleKeyDown);

    this.boundCloseDialog = this.closeDialog.bind(this);
    if (!this.#isPersistent) dialogOverlay?.addEventListener('click', this.boundCloseDialog);
    closeButton?.addEventListener('click', this.boundCloseDialog);

    this.#setupCursorListeners(dialogOverlay);

    // Setup touch handlers for mobile drag gesture
    if (dialogHeader) {
      dialogHeader.addEventListener('touchstart', this.#onTouchStart, { passive: true });
      dialogHeader.addEventListener('touchmove', this.#onTouchMove, { passive: false });
      dialogHeader.addEventListener('touchend', this.#onTouchEnd);
    }

    // Allow a per-dialog drag-to-close threshold via data-drag-threshold (px)
    const attrThreshold = parseFloat(dialog?.getAttribute('data-drag-threshold'));
    if (Number.isFinite(attrThreshold)) this.#dragThreshold = attrThreshold;
  }

  #removeCursorListeners() {
    this.#cursorBinding?.stop();
    this.#cursorBinding = null;
  }

  #setupCursorListeners(overlay) {
    if (isMobileBreakpoint()) return;
    // The custom cursor reads as "click to close", which a persistent dialog does not offer
    if (this.#isPersistent) return;
    const { dialogContainer } = this.#getOwnDialogRefs();
    if (!overlay || !dialogContainer) return;

    this.#cursorBinding = bindCloseCursor({
      root: overlay,
      except: dialogContainer,
      mask: overlay,
    });
  }

  /**
   * Animates dialog from current drag position to bottom of screen
   * Used when closing dialog via drag-to-dismiss gesture
   */
  async #animateDragToClose() {
    const { dialogContainer } = this.#getOwnDialogRefs();
    if (!dialogContainer) return;

    const finalTranslateY = window.innerHeight;

    // Animate to bottom of screen
    dialogContainer.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
    dialogContainer.style.transform = `translateY(${finalTranslateY}px)`;

    // Wait for transition to complete with fallback timeout
    await waitForEvent(dialogContainer, 'transitionend', { once: true }, true, 350)
      .catch(() => {}); // Ignore timeout error, continue execution
  }

  /**
   * @param {Event} [e] - Original event that triggered the close
   */
  async closeDialog(e) {
    e?.preventDefault();
    e?.stopPropagation();

    const { dialog, dialogContainer } = this.#getOwnDialogRefs();
    const isOpen = dialog?.hasAttribute('open');
    if (!isOpen) return;

    this.dispatchEvent(new DialogBeforeCloseEvent(e));

    // Cleanup event listeners
    this.#removeEventListeners();

    const isDragging = dialog.classList.contains('dialog--dragging');
    dialog.classList.remove('dialog--dragging');

    if (!isDragging) {
      dialog.classList.add('dialog-closing');
      dialogContainer.style.transition = 'none';
      dialogContainer.style.transform = '';

      await onAnimationEnd(dialogContainer, undefined, {
        subtree: false,
      });

      dialog.classList.remove('dialog-closing');
    }
    dialog.removeAttribute('open');
    dialogContainer.style.willChange = 'auto';
    dialogContainer.style.removeProperty('transform');
    dialogContainer.style.removeProperty('transition');

    // Restore original position if dialog was appended to body
    if (this.#originalDialogParent) {
      dialog.style.removeProperty('z-index');
      dialog.classList.remove('append-to-body');
      this.#originalDialogParent.appendChild(dialog);
      dialog.removeAttribute('data-dialog-owner');
      this.#originalDialogParent = null;
      this.#appendedDialog = null;
    }

    this.dispatchEvent(new DialogCloseEvent(e));
    this.#updateSectionDialogState(false);
    this.#updateScrollLock(false);
    reviveCloseCursors();

    removeTrapFocus(this.#previousActiveElement ?? null);
    this.#previousActiveElement = null;

    // Restore the focus trap of the dialog still open behind this one.
    this.#findAncestorDialogComponent()?.restoreFocusTrap?.();
  };

  /**
   * Tears down a dialog that was moved to <body> while open, once it has been
   * orphaned by a section re-render (or the component disconnecting). Notifies
   * listeners (DialogCloseEvent), removes the orphan, and resets global state.
   */
  #dismissAppendedDialog() {
    const dialog = this.#appendedDialog;
    this.#appendedDialog = null;
    this.#originalDialogParent = null;
    if (!dialog) return;

    this.#removeEventListeners();
    this.dispatchEvent(new DialogCloseEvent());
    dialog.remove();
    this.#updateScrollLock(false);
    removeTrapFocus(this.#previousActiveElement ?? null);
    this.#previousActiveElement = null;

    // Restore the focus trap of the dialog still open behind this one.
    this.#findAncestorDialogComponent()?.restoreFocusTrap?.();
  }

  toggleDialog() {
    const { dialog } = this.#getOwnDialogRefs();
    if (!dialog) return;
    if (dialog.hasAttribute('open')) {
      this.closeDialog();
    } else {
      this.openDialog();
    }
  }

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    if (this.#isPersistent) return;

    const { dialog } = this.#getOwnDialogRefs();
    if (!dialog) return;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
      event.stopPropagation();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.#isPersistent) return;

    // Only close if this dialog is currently open
    const { dialog } = this.#getOwnDialogRefs();
    if (!dialog?.hasAttribute('open')) return;

    event.preventDefault();
    this.closeDialog(event);
  };

  /**
   * Whether the dialog refuses to be dismissed by the escape key or a click outside of it.
   */
  get #isPersistent() {
    return this.classList.contains('dialog-component--persistent');
  }

  /**
   * Checks if drag gesture is enabled for this dialog
   */
  #isDragEnabled() {
    const { dialog } = this.#getOwnDialogRefs();
    return dialog?.classList.contains('dialog--mobile-bottom') ?? false;
  }

  /**
   * Handles touch start for drag-to-dismiss gesture
   * Only enabled for mobile bottom drawers
   */
  #onTouchStart = (e) => {
    const { dialog, dialogContainer } = this.#getOwnDialogRefs();
    if (!this.#isDragEnabled() || !dialogContainer) return;

    // Record starting position and disable transitions for smooth dragging
    this.#touchStartY = e.touches[0].clientY;
    this.#currentTranslateY = 0;
    this.#dragCloseAt = dialogContainer.getBoundingClientRect().height * DialogComponent.DRAG_CLOSE_RATIO;
    dialogContainer.style.transition = 'none';
    dialog.classList.add('dialog--dragging');
  };

  /**
   * Handles touch move for drag-to-dismiss gesture
   * Only allows downward dragging, uses RAF for smooth animation
   */
  #onTouchMove = (e) => {
    const { dialogContainer } = this.#getOwnDialogRefs();
    if (!this.#isDragEnabled() || !dialogContainer) return;

    const touchY = e.touches[0].clientY;
    const deltaY = touchY - this.#touchStartY;

    // Only allow downward dragging (positive deltaY)
    if (deltaY <= 0) return;

    this.#currentTranslateY = deltaY;

    // Use requestAnimationFrame for smooth 60fps updates
    if (!this.#dragRafId) {
      this.#dragRafId = requestAnimationFrame(() => {
        if (dialogContainer) {
          dialogContainer.style.transform = `translateY(${this.#currentTranslateY}px)`;
        }
        this.#dragRafId = null;
      });
    }

    // Only preventDefault if event is cancelable to avoid browser warnings
    if (e.cancelable) {
      e.preventDefault(); // Prevent page scroll while dragging
    }
  };

  /**
   * Handles touch end for drag-to-dismiss gesture
   * Closes dialog if dragged past threshold, otherwise snaps back
   */
  #onTouchEnd = async () => {
    const { dialog, dialogContainer } = this.#getOwnDialogRefs();

    cancelAnimationFrame(this.#dragRafId);
    this.#dragRafId = null;

    if (!this.#isDragEnabled() || !dialogContainer) return;

    // Close if dragged far enough, otherwise snap back to original position
    if (this.#currentTranslateY > (this.#dragThreshold ?? this.#dragCloseAt)) {
      await this.#animateDragToClose();
      this.closeDialog();
    } else {
      dialog.classList.remove('dialog--dragging');
      dialogContainer.style.transition = 'transform 0.25s ease';
      dialogContainer.style.transform = 'translateY(0)';
    }

    this.#touchStartY = 0;
    this.#currentTranslateY = 0;
  };


  /**
   * Gets the highest z-index from all currently open dialogs (excluding this dialog)
   * @returns {number} The highest z-index, or 99 if no other dialogs are open
   */
  #getHighestZIndex() {
    const openDialogs = document.querySelectorAll('.dialog[open]');
    let highestZIndex = 99; // Base z-index
    const { dialog: currentDialog } = this.#getOwnDialogRefs();

    openDialogs.forEach(dialog => {
      // Skip current dialog
      if (dialog === currentDialog) return;

      const dialogSection = dialog.closest('.shopify-section');
      if (dialogSection) {
        const zIndex = parseInt(dialogSection.style.zIndex, 10);
        if (!isNaN(zIndex) && zIndex > highestZIndex) {
          highestZIndex = zIndex;
        }
      } else if (dialog.classList.contains('append-to-body')) {
        const zIndex = parseInt(dialog.style.zIndex, 10);
        if (!isNaN(zIndex) && zIndex > highestZIndex) {
          highestZIndex = zIndex;
        }
      }
    });

    return highestZIndex;
  }

  /**
   * Manages z-index stacking for nested dialogs
   * @param {boolean} isOpen - Whether dialog is opening or closing
   * @param {boolean} isDialogComponent - Whether dialog is a dialog component
   */
  #updateSectionDialogState(isOpen) {
    if (this.#shouldAppendToBody()) return;
    const section = this.closest('.shopify-section');
    if (!section) return;
    if(section.closest('.content-for-layout')) return;

    if (isOpen) {
      const highestZIndex = this.#getHighestZIndex();
      this.#zIndex = highestZIndex + 1;
      section.classList.add('relative');
      section.style.zIndex = this.#zIndex;
    } else {
      // Keep z-index if nested dialog is still open
      const parentDialog = this.closest('.dialog');
      if(parentDialog && parentDialog.hasAttribute('open')) {
        section.style.zIndex = this.#zIndex;
      } else {
        if (this.#zIndex > 0) {
          section.style.zIndex = '';
        }
        section.classList.remove('relative');
      }
    }
  }

  /**
   * Checks if there are other dialogs open (excluding nested dialogs in this component)
   * @returns {boolean} True if there are other dialogs open
   */
  hasOtherDialogsOpen() {
    return document.querySelectorAll('.dialog[open]').length > 0;
  }

  #updateScrollLock(isOpen) {
    if (isOpen) {
      document.documentElement.setAttribute('scroll-lock', '');
      return;
    }
    if (!this.hasOtherDialogsOpen()) {
      document.documentElement.removeAttribute('scroll-lock');

      if (this.documentScrollBarWidth !== this.currentScrollBarWidth) {
        document.documentElement.style.setProperty('--scrollbar-width', `${this.currentScrollBarWidth}px`);
      }
    }
  }

  /**
   * Sets document --scrollbar-width CSS variable to current scrollbar width when they differ.
   */
  #calculateScrollbarWidth() {
    if (this.documentScrollBarWidth !== this.currentScrollBarWidth && this.currentScrollBarWidth > 0) {
      document.documentElement.style.setProperty('--scrollbar-width', `${this.currentScrollBarWidth}px`);
    }
  }

  get documentScrollBarWidth() {
    return document.documentElement.style.getPropertyValue('--scrollbar-width');
  }

  get currentScrollBarWidth() {
    return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
  }

  get shouldHandleDesignMode() {
    return this.hasAttribute('handle-design-mode');
  }
}

if (!customElements.get('dialog-component')) {
  customElements.define('dialog-component', DialogComponent);
}

class DropdownDialogComponent extends Component {
  static DRAG_CLOSE_RATIO = 0.2;
  static OPEN_DELAY = 100;
  static CLOSE_DELAY = 250;
  static VIEWPORT_PADDING = 8;
  static DIALOG_BUFFER = 100;
  static FIXED_OFFSET = 15;
  static SCROLL_CLOSE_THRESHOLD = 8;

  #touchStartY = 0;
  #currentTranslateY = 0;
  #dragThreshold = null;
  #dragCloseAt = 0;
  #dragRafId = null;
  #positionRafId = null;

  #hoverOpenTimer = null;
  #hoverCloseTimer = null;
  #isOpen = false;
  #closeAnim = null;
  /** @type {Node | null} */
  #originalDialogParent = null;
  #originalDialogStyle = null;
  #dialogSize = null;
  #isAppendedToBody = false;
  #scrollCloseFrom = null;
  #refListenersBound = false;

  boundCloseDialog = this.closeDialog.bind(this);

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#handleResize);
    this.isHidden = true;

    this.#bindRefListeners();

    if (this.dataset.action === 'hover') {
      this.addEventListener('mouseenter', this.#handleMouseEnter);
      this.addEventListener('mouseleave', this.#handleMouseLeave);
    }

    document.addEventListener(DialogOpenEvent.eventName, this.boundCloseDialog);
  }

  /**
   * Binds every listener that hangs off a ref. The parser upgrades this element at its opening
   * tag, so `refs` can still be empty here and the toggle button ends up inert — this runs
   * again from `refsUpdatedCallback()` once the children turn up.
   */
  #bindRefListeners() {
    const { toggleButton, dialog, dialogHeader, closeButton } = this.refs;
    if (this.#refListenersBound || !toggleButton) return;
    this.#refListenersBound = true;

    if (dialogHeader) {
      dialogHeader.addEventListener('touchstart', this.#onTouchStart, { passive: true });
      dialogHeader.addEventListener('touchmove', this.#onTouchMove, { passive: false });
      dialogHeader.addEventListener('touchend', this.#onTouchEnd);
    }

    toggleButton.addEventListener('click', this.#handleClick);
    toggleButton.addEventListener('pointerdown', this.#handlePointerDown);

    // Ensure hover-open behavior works even when the dialog is appended to <body>
    // (footer) by listening on the dialog itself too.
    if (this.dataset.action === 'hover') {
      dialog?.addEventListener('mouseenter', this.#handleDialogMouseEnter);
      dialog?.addEventListener('mouseleave', this.#handleDialogMouseLeave);
    }

    closeButton?.addEventListener('click', this.boundCloseDialog);
  }

  refsUpdatedCallback() {
    this.#bindRefListeners();
  }

  #handlePointerDown = (e) => {
    this._lastPointerType = e.pointerType;
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.#unbindScrollClose();
    clearTimeout(this.#hoverOpenTimer);
    this.#hoverOpenTimer = null;
    clearTimeout(this.#hoverCloseTimer);
    this.#hoverCloseTimer = null;
    const { toggleButton, dialog, dialogHeader, closeButton } = this.refs;
    if (dialogHeader) {
      dialogHeader.removeEventListener('touchstart', this.#onTouchStart);
      dialogHeader.removeEventListener('touchmove', this.#onTouchMove);
      dialogHeader.removeEventListener('touchend', this.#onTouchEnd);
    }
    document.removeEventListener(DialogOpenEvent.eventName, this.boundCloseDialog);
    closeButton?.removeEventListener('click', this.boundCloseDialog);
    this.removeEventListener('keydown', this.#handleKeyDown);
    toggleButton?.removeEventListener('click', this.#handleClick);
    toggleButton?.removeEventListener('pointerdown', this.#handlePointerDown);
    if (this.dataset.action === 'hover') {
      this.removeEventListener('mouseenter', this.#handleMouseEnter);
      this.removeEventListener('mouseleave', this.#handleMouseLeave);

      dialog?.removeEventListener('mouseenter', this.#handleDialogMouseEnter);
      dialog?.removeEventListener('mouseleave', this.#handleDialogMouseLeave);
    }
    this.#refListenersBound = false;
  }

  #handleClick = (e) => {
    if (this.dataset.action === 'hover' && isDesktopBreakpoint()) {
      const isMouseOrPen = this._lastPointerType === 'mouse' || this._lastPointerType === 'pen';
      if (isMouseOrPen) return;
    }
    e?.preventDefault();
    this.openDialog(e);
  };

  #handleMouseEnter = () => {
    if (!isDesktopBreakpoint()) return;

    clearTimeout(this.#hoverCloseTimer);
    this.#hoverCloseTimer = null;

    clearTimeout(this.#hoverOpenTimer);
    this.#hoverOpenTimer = setTimeout(() => {
      this.#hoverOpenTimer = null;
      if (this.#isOpen) return;
      this.#closeSiblingDropdownDialogs();
      this.showResults();
    }, DropdownDialogComponent.OPEN_DELAY);
  };

  #handleDialogMouseEnter = () => {
    clearTimeout(this.#hoverCloseTimer);
    this.#hoverCloseTimer = null;
  };

  #handleDialogMouseLeave = (e) => {
    // Treat leaving the (appended-to-body) dialog the same as leaving the trigger wrapper.
    this.#handleMouseLeave(e);
  };

  #handleMouseLeave = (e) => {
    if (!isDesktopBreakpoint()) return;

    clearTimeout(this.#hoverOpenTimer);
    this.#hoverOpenTimer = null;

    clearTimeout(this.#hoverCloseTimer);
    this.#hoverCloseTimer = null;

    const { dialog } = this.refs;
    const relatedTarget = e?.relatedTarget;

    // Keep open while moving between trigger and dialog.
    // When appended to body, `relatedTarget` can be null in some cases.
    if (relatedTarget) {
      if (dialog?.contains?.(relatedTarget)) return;
      if (this.contains(relatedTarget)) return;
    }

    this.#hoverCloseTimer = setTimeout(() => {
      this.#hoverCloseTimer = null;

      // Safety net: if pointer is still over trigger or dialog, don't close.
      if (dialog?.matches?.(':hover')) return;
      if (this.matches?.(':hover')) return;

      this.hideResults();
    }, DropdownDialogComponent.CLOSE_DELAY);
  };


  openDialog(e) {
    e?.preventDefault();

    if (!this.#isOpen) {
      this.#closeSiblingDropdownDialogs();
    }

    // If opened via click/tap (no mouse), force click mode behavior.
    if (this.dataset.action === 'hover') {
      const isMouseOrPen = this._lastPointerType === 'mouse' || this._lastPointerType === 'pen';
      if (!isMouseOrPen) {
        this.dataset.action = 'click';
      }
    }

    return this.#isOpen ? this.hideResults() : this.showResults();
  }

  closeDialog() {
    this.hideResults();
  }

  #handleResize = debounce(() => {
    if (this.#isOpen) {
      this.#schedulePosition();
    }
  }, 100);

  #closeSiblingDropdownDialogs() {
    const parent = this.parentElement;
    if (!parent) return;

    parent.querySelectorAll('dropdown-dialog').forEach((el) => {
      if (el === this) return;
      el.closeDialog?.();
    });
  }

  #onTouchStart = (e) => {
    const dialog = this.refs.dialog;
    const container = this.refs.dialogContainer;

    this.#touchStartY = e.touches[0].clientY;
    this.#currentTranslateY = 0;
    this.#dragCloseAt = container.getBoundingClientRect().height * DropdownDialogComponent.DRAG_CLOSE_RATIO;
    container.style.transition = 'none';
    dialog.classList.add('dialog--dragging');
  };

  #onTouchMove = (e) => {
    const container = this.refs.dialogContainer;

    const touchY = e.touches[0].clientY;
    const deltaY = touchY - this.#touchStartY;

    if (deltaY <= 0) return; // ignore upward movement

    this.#currentTranslateY = deltaY;

    if (!this.#dragRafId) {
      this.#dragRafId = requestAnimationFrame(() => {
        container.style.transform = `translateY(${this.#currentTranslateY}px)`;
        this.#dragRafId = null;
      });
    }

    e.preventDefault(); // block scroll while dragging
  };

  #onTouchEnd = () => {
    const dialog = this.refs.dialog;
    const container = this.refs.dialogContainer;

    cancelAnimationFrame(this.#dragRafId);
    this.#dragRafId = null;

    dialog.classList.remove('dialog--dragging');

    if (this.#currentTranslateY > (this.#dragThreshold ?? this.#dragCloseAt)) {
      this.closeDialog();
      container.style.transition = '';
      container.style.transform = '';
    } else {
      container.style.transition = 'transform 0.25s ease';
      container.style.transform = 'translateY(0)';
    }

    this.#touchStartY = 0;
    this.#currentTranslateY = 0;
  };

  #computeShiftX(rect, buttonRect, viewportPadding) {
    // Match menu dropdown level 1: only shift when overflowing on the right,
    // anchoring the dropdown's right edge to the button.
    if (rect.right > window.innerWidth - viewportPadding) {
      return Math.floor(buttonRect.right - rect.right);
    }

    return 0;
  }

  #setVerticalPosition(dialog, buttonRect, dialogHeight) {
    const buffer = DropdownDialogComponent.DIALOG_BUFFER;
    const spaceBelow = window.innerHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    const useTopAlign = spaceBelow >= (dialogHeight + buffer) || spaceBelow > spaceAbove;

    dialog.classList.toggle('dropdown-dialog--bottom', !useTopAlign);
  }

  /**
   * Auto-position the dropdown based on available space in viewport
   */
  #autoPosition() {
    const { dialog, toggleButton } = this.refs;
    if (!dialog || !toggleButton) return;

    const viewportPadding = DropdownDialogComponent.VIEWPORT_PADDING;

    const buttonRect = toggleButton.getBoundingClientRect();
    const dialogHeight = this.#dialogSize?.height ?? dialog.offsetHeight;

    // Reset previous state
    dialog.classList.remove('dropdown-dialog--bottom');
    dialog.style.removeProperty('--dropdown-dialog-shift-x');

    // Match menu dropdown level 1 behavior: only shift on right overflow.
    const rect = dialog.getBoundingClientRect();
    const shiftX = this.#computeShiftX(rect, buttonRect, viewportPadding);

    if (shiftX !== 0) {
      dialog.style.setProperty('--dropdown-dialog-shift-x', `${shiftX}px`);
    }

    this.#setVerticalPosition(dialog, buttonRect, dialogHeight);
  }


  #isInFooterSection() {
    return !!this.closest('.footer-section');
  }

  #appendBody() {
    if (this.#isAppendedToBody) return;
    if (!this.#isInFooterSection()) return;

    const { dialog } = this.refs;
    if (!dialog) return;
    this.#originalDialogParent = dialog.parentNode;
    this.#originalDialogStyle = dialog.getAttribute('style');
    document.body.appendChild(dialog);
    this.#isAppendedToBody = true;

  }

  #restoreParent() {
    if (!this.#isAppendedToBody) return;
    const { dialog } = this.refs;
    if (!dialog) return;


    if (this.#originalDialogParent) {
      this.#originalDialogParent.appendChild(dialog);
    }

    if (this.#originalDialogStyle == null) {
      dialog.removeAttribute('style');
    } else {
      dialog.setAttribute('style', this.#originalDialogStyle);
    }

    this.#originalDialogParent = null;
    this.#originalDialogStyle = null;
    this.#isAppendedToBody = false;
  }

  /**
   * The appended-to-body dialog is positioned once against the viewport, so it cannot follow
   * its trigger: in a plain footer the button scrolls away from it, and with the scroll-reveal
   * footer (`position: sticky`) the button stays put while the page moves. Native `<select>`
   * settles this the same way — the popup closes as soon as the page scrolls.
   */
  #bindScrollClose() {
    if (!this.#isAppendedToBody) return;
    if (isMobileBreakpoint()) return; // mobile dialog is a full-screen sheet, unaffected by scroll
    if (this.#scrollCloseFrom !== null) return;

    this.#scrollCloseFrom = window.scrollY;
    window.addEventListener('scroll', this.#handleScroll, { passive: true });
  }

  #unbindScrollClose() {
    if (this.#scrollCloseFrom === null) return;

    this.#scrollCloseFrom = null;
    window.removeEventListener('scroll', this.#handleScroll);
  }

  #handleScroll = () => {
    if (!this.#isOpen) return;

    // Ignore jitter and momentum settling so a stray wheel tick doesn't drop the dialog.
    const travelled = Math.abs(window.scrollY - this.#scrollCloseFrom);
    if (travelled < DropdownDialogComponent.SCROLL_CLOSE_THRESHOLD) return;

    this.closeDialog();
  };

  #setFixedPosition() {
    if (!this.#isAppendedToBody) return;
    if (isMobileBreakpoint()) return; // mobile dialog is already `position: fixed`

    const { dialog, toggleButton } = this.refs;
    if (!dialog || !toggleButton) return;

    const viewportPadding = DropdownDialogComponent.VIEWPORT_PADDING;

    const buttonRect = toggleButton.getBoundingClientRect();
    const dialogHeight = this.#dialogSize?.height ?? dialog.offsetHeight;
    const dialogWidth = this.#dialogSize?.width ?? dialog.offsetWidth;

    const isBottomAlign = dialog.classList.contains('dropdown-dialog--bottom');

    const baselineLeft = buttonRect.left;
    const baselineTop = isBottomAlign
      ? buttonRect.top - dialogHeight - DropdownDialogComponent.FIXED_OFFSET
      : buttonRect.bottom + DropdownDialogComponent.FIXED_OFFSET;

    // Apply baseline fixed positioning first, then measure and shift like non-appended.
    dialog.style.position = 'fixed';
    dialog.style.inset = 'auto';
    dialog.style.left = `${Math.round(baselineLeft)}px`;
    dialog.style.top = `${Math.round(Math.max(viewportPadding, baselineTop))}px`;
    dialog.style.right = 'auto';
    dialog.style.bottom = 'auto';
    dialog.style.zIndex = '1000';

    const rect = dialog.getBoundingClientRect();

    const shiftX = this.#computeShiftX(rect, buttonRect, viewportPadding);

    // Clamp fixed left to viewport (only needed for appended-to-body).
    const finalLeft = Math.max(
      viewportPadding,
      Math.min(baselineLeft + shiftX, window.innerWidth - viewportPadding - dialogWidth)
    );

    dialog.style.left = `${Math.round(finalLeft)}px`;

    // Fixed positioning uses left/top, so ensure we don't keep transform-based shifting.
    dialog.style.removeProperty('--dropdown-dialog-shift-x');
  }

  #setPosition() {
    const { dialog, toggleButton } = this.refs;
    if (dialog) {
      // Cache real rendered size (dialog may be display:none before [open]).
      this.#dialogSize = { width: dialog.offsetWidth, height: dialog.offsetHeight };
    }

    if (this.#isAppendedToBody) {
      if (!dialog || !toggleButton) return;

      // Only compute vertical alignment, then apply fixed positioning.
      dialog.classList.remove('dropdown-dialog--bottom');
      const buttonRect = toggleButton.getBoundingClientRect();
      const dialogHeight = this.#dialogSize?.height ?? dialog.offsetHeight;
      this.#setVerticalPosition(dialog, buttonRect, dialogHeight);

      this.#setFixedPosition();
      return;
    }

    this.#autoPosition();
  }

  #schedulePosition() {
    cancelAnimationFrame(this.#positionRafId);
    this.#positionRafId = requestAnimationFrame(() => {
      this.#positionRafId = null;
      this.#setPosition();
    });
  }

  async showResults() {
    const { dialog, toggleButton, dialogInner, dialogContainer } = this.refs;

    if (this.#isOpen) return;
    this.#isOpen = true;

    // Re-entering during close animation: cancel it and restore
    if (this.#closeAnim) {
      this.#closeAnim.cancel();
      this.#closeAnim = null;
      dialog.style.opacity = '';
      toggleButton.setAttribute('aria-expanded', 'true');
      if (!this.boundHandleClickOutside) {
        this.boundHandleClickOutside = this.#handleClickOutside.bind(this);
      }
      document.addEventListener('click', this.boundHandleClickOutside);
      this.addEventListener('keydown', this.#handleKeyDown);
      this.#bindScrollClose();
      this.dispatchEvent(new DropdownDialogOpenEvent());
      return;
    }

    this.#appendBody();
    this.#bindScrollClose();

    if (dialogInner) {
      const templateElement = this.refs.dialogTemplate;
      if (templateElement) {
        const templateHTML = templateElement.content.cloneNode(true);
        dialogInner.innerHTML = '';
        dialogInner.appendChild(templateHTML);
      }
    }

    // Size is cached on first position pass (after open).
    this.#dialogSize = null;

    // Store handler reference for cleanup
    if (!this.boundHandleClickOutside) {
      this.boundHandleClickOutside = this.#handleClickOutside.bind(this);
    }
    document.addEventListener('click', this.boundHandleClickOutside);
    this.addEventListener('keydown', this.#handleKeyDown);

    dialog.setAttribute('open', '');
    toggleButton.setAttribute('aria-expanded', 'true');

    // Position dropdown based on available space
    this.#schedulePosition();

    if(isMobileBreakpoint()) {
      await onAnimationEnd(dialogContainer, undefined, {
        subtree: false,
      });
      dialog.style.willChange = 'auto';
      this.dispatchEvent(new DropdownDialogOpenEvent());
      requestAnimationFrame(() => {
        dialog.classList.add('dropdown-dialog--animate');
      });
      return;
    }

    this.dispatchEvent(new DropdownDialogOpenEvent());

    // Delay opening when inside a transparent non-scrolling header so the background
    // animation appears first. When scrolling, the header already has a background
    // so no delay is needed.
    const headerComp = this.closest('header-component.header--transparent');
    const needsDelay = !!headerComp && !headerComp.closest('.header-section')?.classList.contains('header-scrolling');

    animate(
      dialog,
      { opacity: [0,1] },
      { duration: 200, easing: 'ease-out', delay: needsDelay ? 200 : 0 }
    );

    requestAnimationFrame(() => {
      dialog.classList.add('dropdown-dialog--animate');
    });
  }

  async hideResults() {
    const { dialog, toggleButton, dialogContainer } = this.refs;

    if (!this.#isOpen) return;
    this.#isOpen = false;

    this.#unbindScrollClose();

    // Remove click outside listener when closing
    if (this.boundHandleClickOutside) {
      document.removeEventListener('click', this.boundHandleClickOutside);
      this.boundHandleClickOutside = null;
    }
    this.removeEventListener('keydown', this.#handleKeyDown);

    if(isMobileBreakpoint()) {
      dialog.classList.add('dialog-closing');
      dialogContainer.style.transition = 'none';
      dialogContainer.style.transform = '';
      this.dispatchEvent(new DropdownDialogCloseEvent());

      await onAnimationEnd(dialogContainer, undefined, {
        subtree: false,
      });
      toggleButton?.setAttribute('aria-expanded', 'false');
      dialog.removeAttribute('open');
      dialog.classList.remove('dialog-closing');
      dialogContainer.style.removeProperty('will-change');
      dialogContainer.style.removeProperty('transition');
      dialogContainer.style.removeProperty('transform');
      dialog.classList.remove('dropdown-dialog--animate');
      this.#dialogSize = null;
      this.#restoreParent();
      return;
    }

    this.#closeAnim = animate(
      dialog,
      { opacity: [1,0] },
      { duration: 200, easing: 'ease-out', fill: 'none' }
    );

    this.#closeAnim.finished.then(() => {
      this.#closeAnim = null;
      if (!this.#isOpen) {
        toggleButton?.setAttribute('aria-expanded', 'false');
        dialog.removeAttribute('open');
        dialog.classList.remove('dialog-closing');
        dialog.classList.remove('dropdown-dialog--animate');
        this.#dialogSize = null;
        this.#restoreParent();
      }
    });

    this.dispatchEvent(new DropdownDialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  };

    /**
   * Handles the click outside event.
   *
   * @param {PointerEvent} event - The event object.
   */
    #handleClickOutside = (event) => {
      const { dialog } = this.refs;
      const isOverlay = event.target?.classList?.contains('dropdown-dialog__overlay');

      // Overlay click should always close (even when dialog is appended to body).
      if (isOverlay) {
        this.hideResults();
        return;
      }

      // When appended to body, clicks inside the dialog won't be contained by `this`.
      if (isClickedOutside(event, this)) {
        if (dialog?.contains?.(event.target)) return;
        this.hideResults();
      }
    };
}

if (!customElements.get('dropdown-dialog')) {
  customElements.define('dropdown-dialog', DropdownDialogComponent);
}